import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CreditCard, Save, X, RotateCcw, Star } from 'lucide-react';
import api from '../../lib/api';

const TIPOS = [
  { val: 'credito', label: 'Crédito' },
  { val: 'debito', label: 'Débito' },
];

const MARCAS = [
  'Visa',
  'Mastercard',
  'American Express',
  'Diners',
  'Redcompra',
  'Otra',
];

const fmtCLP = (n) => {
  if (n === null || n === undefined || n === '') return '';
  const num = Number(n);
  if (Number.isNaN(num)) return '';
  return num.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

const empty = {
  codigo: '', etiqueta: '', banco: '', ultimos_4: '', titular: '',
  tipo: 'credito', marca: '', cupo: '', vencimiento: '', observaciones: '',
  activo: true, favorita: false,
};

export default function TarjetasPagoPage() {
  const [tarjetas, setTarjetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incluirInactivas, setIncluirInactivas] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editCodigo, setEditCodigo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tarjetas-pago', { params: { incluir_inactivas: incluirInactivas } });
      setTarjetas(res.data || []);
    } catch {
      toast.error('Error cargando tarjetas');
    } finally {
      setLoading(false);
    }
  }, [incluirInactivas]);

  useEffect(() => { load();   }, [load]);

  const openNew = () => {
    // Próximo código tc(N+1)
    const maxN = tarjetas
      .map((t) => parseInt(String(t.codigo).replace(/^tc/i, ''), 10))
      .filter((n) => !Number.isNaN(n))
      .reduce((a, b) => Math.max(a, b), 0);
    const nextCodigo = `tc${maxN + 1}`;
    setForm({ ...empty, codigo: nextCodigo, etiqueta: `Tarjeta de Crédito ${maxN + 1}` });
    setEditCodigo(null);
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setForm({
      codigo: t.codigo || '',
      etiqueta: t.etiqueta || '',
      banco: t.banco || '',
      ultimos_4: t.ultimos_4 || '',
      titular: t.titular || '',
      tipo: t.tipo || 'credito',
      marca: t.marca || '',
      cupo: t.cupo ?? '',
      vencimiento: t.vencimiento || '',
      observaciones: t.observaciones || '',
      activo: t.activo !== false,
      favorita: !!t.favorita,
    });
    setEditCodigo(t.codigo);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.codigo.trim() || !form.etiqueta.trim()) {
      toast.error('Código y etiqueta son obligatorios');
      return;
    }
    const payload = {
      ...form,
      codigo: form.codigo.trim(),
      etiqueta: form.etiqueta.trim(),
      cupo: form.cupo === '' ? null : Number(form.cupo),
    };
    try {
      if (editCodigo) {
        await api.put(`/tarjetas-pago/${encodeURIComponent(editCodigo)}`, payload);
        toast.success(`Tarjeta ${payload.etiqueta} actualizada`);
      } else {
        await api.post('/tarjetas-pago', payload);
        toast.success(`Tarjeta ${payload.etiqueta} creada`);
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleToggleActive = async (t) => {
    try {
      if (t.activo === false) {
        await api.post(`/tarjetas-pago/${encodeURIComponent(t.codigo)}/reactivar`);
        toast.success(`Tarjeta ${t.etiqueta} reactivada`);
      } else {
        if (!window.confirm(`¿Desactivar la tarjeta "${t.etiqueta}"? No se borrarán datos, solo se ocultará.`)) return;
        await api.delete(`/tarjetas-pago/${encodeURIComponent(t.codigo)}`);
        toast.success(`Tarjeta ${t.etiqueta} desactivada`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="space-y-5" data-testid="tarjetas-pago-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-indigo-600" />
            Tarjetas / Medios de Pago
          </h1>
          <p className="text-sm text-muted-foreground">
            Administra las tarjetas de crédito/débito usadas en OC Express y aprobación de compras.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={incluirInactivas}
              onChange={(e) => setIncluirInactivas(e.target.checked)}
              className="accent-indigo-600"
              data-testid="chk-incluir-inactivas"
            />
            Mostrar inactivas
          </label>
          <Button onClick={openNew} className="gap-2" data-testid="btn-nueva-tarjeta">
            <Plus className="h-4 w-4" /> Nueva Tarjeta
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Listado ({tarjetas.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : tarjetas.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No hay tarjetas configuradas. Presiona "Nueva Tarjeta" para agregar la primera.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center w-12">★</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Etiqueta</TableHead>
                    <TableHead>Banco</TableHead>
                    <TableHead>Marca</TableHead>
                    <TableHead>**** Últimos 4</TableHead>
                    <TableHead>Titular</TableHead>
                    <TableHead className="text-right">Cupo</TableHead>
                    <TableHead>Vence</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tarjetas.map((t) => (
                    <TableRow key={t.codigo} className={t.activo === false ? 'opacity-60' : ''} data-testid={`row-tarjeta-${t.codigo}`}>
                      <TableCell className="text-center" title={t.favorita ? 'Tarjeta favorita (sale como botón rápido)' : 'Tarjeta común (sale en dropdown)'}>
                        {t.favorita ? (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-400 mx-auto" />
                        ) : (
                          <Star className="h-3.5 w-3.5 text-gray-300 mx-auto" />
                        )}
                      </TableCell><TableCell className="font-mono text-xs">{t.codigo}</TableCell>
                      <TableCell className="font-medium">{t.etiqueta}</TableCell>
                      <TableCell>{t.banco || '—'}</TableCell>
                      <TableCell>{t.marca || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {t.ultimos_4 ? `**** ${t.ultimos_4}` : '—'}
                      </TableCell>
                      <TableCell>{t.titular || '—'}</TableCell>
                      <TableCell className="text-right text-xs">{t.cupo ? fmtCLP(t.cupo) : '—'}</TableCell>
                      <TableCell className="text-xs">{t.vencimiento || '—'}</TableCell>
                      <TableCell className="text-center">
                        {t.activo === false ? (
                          <Badge variant="outline" className="text-orange-600 border-orange-300">Inactiva</Badge>
                        ) : (
                          <Badge className="bg-green-600 hover:bg-green-700">Activa</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openEdit(t)}
                            title="Editar"
                            data-testid={`btn-edit-${t.codigo}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${t.activo === false ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}`}
                            onClick={() => handleToggleActive(t)}
                            title={t.activo === false ? 'Reactivar' : 'Desactivar'}
                            data-testid={`btn-toggle-${t.codigo}`}
                          >
                            {t.activo === false ? <RotateCcw className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              {editCodigo ? `Editar ${editCodigo}` : 'Nueva tarjeta'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Código *</Label>
              <Input
                value={form.codigo}
                onChange={(e) => setForm((p) => ({ ...p, codigo: e.target.value }))}
                placeholder="tc1"
                disabled={!!editCodigo}
                data-testid="input-codigo"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Etiqueta *</Label>
              <Input
                value={form.etiqueta}
                onChange={(e) => setForm((p) => ({ ...p, etiqueta: e.target.value }))}
                placeholder="Tarjeta de Crédito 1"
                data-testid="input-etiqueta"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Banco</Label>
              <Input value={form.banco} onChange={(e) => setForm((p) => ({ ...p, banco: e.target.value }))} placeholder="Banco Chile" data-testid="input-banco" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Marca</Label>
              <Select value={form.marca || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, marca: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="select-marca"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {MARCAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Últimos 4 dígitos</Label>
              <Input
                value={form.ultimos_4}
                onChange={(e) => setForm((p) => ({ ...p, ultimos_4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                placeholder="4532"
                maxLength={4}
                data-testid="input-ultimos-4"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((p) => ({ ...p, tipo: v }))}>
                <SelectTrigger data-testid="select-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t.val} value={t.val}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Titular</Label>
              <Input value={form.titular} onChange={(e) => setForm((p) => ({ ...p, titular: e.target.value }))} placeholder="Nombre titular" data-testid="input-titular" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cupo ($ CLP)</Label>
              <Input
                type="number"
                value={form.cupo}
                onChange={(e) => setForm((p) => ({ ...p, cupo: e.target.value }))}
                placeholder="5000000"
                data-testid="input-cupo"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Vencimiento (MM/YY)</Label>
              <Input
                value={form.vencimiento}
                onChange={(e) => setForm((p) => ({ ...p, vencimiento: e.target.value }))}
                placeholder="05/28"
                maxLength={5}
                data-testid="input-vencimiento"
              />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Observaciones</Label>
              <Input value={form.observaciones} onChange={(e) => setForm((p) => ({ ...p, observaciones: e.target.value }))} placeholder="Notas internas" data-testid="input-observaciones" />
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <input
                id="activo-chk"
                type="checkbox"
                checked={form.activo}
                onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
                className="accent-indigo-600"
                data-testid="chk-activo"
              />
              <Label htmlFor="activo-chk" className="text-sm cursor-pointer">Tarjeta activa (visible en OC Express)</Label>
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <input
                id="favorita-chk"
                type="checkbox"
                checked={form.favorita}
                onChange={(e) => setForm((p) => ({ ...p, favorita: e.target.checked }))}
                className="accent-amber-500"
                data-testid="chk-favorita"
              />
              <Label htmlFor="favorita-chk" className="text-sm cursor-pointer flex items-center gap-1.5">
                <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
                Marcar como favorita
                <span className="text-xs text-muted-foreground font-normal">(aparecerá como botón rápido en OC Express; si no, queda en el dropdown "Más tarjetas")</span>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="gap-2" data-testid="btn-cancelar">
              <X className="h-4 w-4" /> Cancelar
            </Button>
            <Button onClick={handleSave} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="btn-guardar">
              <Save className="h-4 w-4" /> {editCodigo ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
